/**
 * Gateway Data Transfer Object
 * Represents a normalized Gateway entity with validation
 */

export interface IGatewayData {
  uid?: string;
  id?: string;
  groupId?: string;
  name?: string;
  gatewayAppName?: string;
  gatewayName?: string;
  deviceIds?: string[];
  status?: string;
  devices?: any[]; // Raw devices from API (will be processed elsewhere)
}

export type GatewayStatus = 'REGISTERED' | 'DEREGISTERED' | 'UNKNOWN';

export class GatewayDTO {
  /** Registries created before the status field existed have none -> UNKNOWN */
  private static resolveStatus(status?: string): GatewayStatus {
    return status === 'REGISTERED' || status === 'DEREGISTERED' ? status : 'UNKNOWN';
  }

  readonly uid: string;
  readonly groupId: string;
  readonly name: string;
  readonly gatewayAppName: string | null;
  readonly deviceIds: string[];
  readonly status: GatewayStatus;

  constructor(data: IGatewayData) {
    this.uid = this.validateRequired(
      data.id || data.uid || data.groupId,
      'uid'
    );
    this.groupId = data.id || data.groupId || this.uid;
    this.name = this.resolveName(data);
    this.gatewayAppName = data.gatewayAppName || null;
    this.status = GatewayDTO.resolveStatus(data.status);
    this.deviceIds = Array.isArray(data.deviceIds) 
      ? data.deviceIds 
      : Array.isArray(data.devices)
        ? data.devices
            .map((d: any) => d.uid || d.id || d.deviceId)
            .filter((id): id is string => Boolean(id))
        : [];
  }

  private validateRequired(value: any, fieldName: string): string {
    if (value === undefined || value === null || value === '') {
      throw new Error(`GatewayDTO: ${fieldName} is required`);
    }
    return String(value);
  }

  /**
   * Clear priority for gateway name resolution
   * Priority: gatewayAppName > gatewayName > name > uid
   */
  private resolveName(data: IGatewayData): string {
    if (data.gatewayAppName) return data.gatewayAppName;
    if (data.gatewayName) return data.gatewayName;
    if (data.name) return data.name;
    return this.uid;
  }

  /**
   * Create DTO from API response
   */
  static fromApiResponse(apiData: IGatewayData): GatewayDTO {
    return new GatewayDTO(apiData);
  }

  /**
   * Create multiple DTOs from API response array
   */
  static fromApiResponseArray(apiDataArray: any): GatewayDTO[] {
    if (!Array.isArray(apiDataArray)) {
      console.warn('GatewayDTO.fromApiResponseArray: expected array');
      return [];
    }
    return apiDataArray.map(data => GatewayDTO.fromApiResponse(data));
  }

  /**
   * Convert to plain object for store
   */
  toStoreEntity() {
    return {
      uid: this.uid,
      groupId: this.groupId,
      name: this.name,
      gatewayAppName: this.gatewayAppName,
      deviceIds: this.deviceIds,
      status: this.status,
    };
  }
}
